import { AgentSkills, KeyValue, SkillType } from './AiAgentV2';
import { createFolderService, uploadFileService } from '../services/AssetService';
import { OldValuesSkills } from 'src/components/pages/ai-agent-v2/agentSetupComponent/agentSkillsComponent/AgentSkillsComponent';
import { validateAndTranslate } from 'src/utils';
import { v4 as uuidv4 } from 'uuid';
import { learnAgentServiceV2, upsertAgentSkillServiceV2 } from '../services/AgentService';
import { EnumSkillRetrieverFields } from './Common';


export class AgentSkillClass {
    private _skill: AgentSkills;
    private knowledge = '';
    private is_structure_retriever: boolean = false;
    private old_values: OldValuesSkills = {};
    private skillRetrieverSelected: string = ""

    constructor(data: Partial<AgentSkills>, knowledge: string, data2: {retriever?: boolean, oldValues?: OldValuesSkills}, skillRetrieverSelected?:string) {
        this._skill = data as AgentSkills;
        this.knowledge = knowledge;
        this.skillRetrieverSelected = skillRetrieverSelected || "";
        if(data2.retriever !== undefined) {
            this.is_structure_retriever = data2.retriever;
        }

        if(data2.oldValues) {
            this.old_values = data2.oldValues;
        }

        this.initializeConfig();

    }

    private initializeConfig() {

        if(this._skill.skill_type === SkillType.SkillStructuredRetriever && this._skill.config) {
            this._skill = {
                ...this._skill,
                config: {
                    ...this._skill.config,
                    vector_store_config: {},
                    vector_store: ""
                }
            }  
        }

        if(this._skill.skill_type === SkillType.SkillStructuredRetriever || this._skill.skill_type === SkillType.SkillUnstructuredRetriever) {
            this._skill = {
                ...this._skill,
                skill_type: SkillType.SkillRetriever
            }
        }

        if (!this._skill.config) {
            this._skill = {
                ...this._skill,
                config: {
                    config_type: this._skill.skill_type as SkillType,
                },
            };
        }

        if (this._skill.config && !this._skill.config.config_type) {
            this._skill = {
                ...this._skill,
                config: {
                    ...this._skill.config,
                    config_type: this._skill.skill_type as SkillType,
                },
            };
        }

        // Add config type to Document Analysis
        if (this._skill.skill_type === SkillType.SkillDocumentAnalysis) {
            if (this._skill.config.summarize_config) {
                this._skill.config.summarize_config.config_type = SkillType.SkillSummarize;
            }

            if (this._skill.config.sentiment_config) {
                if ((this._skill.config.sentiment_config?.threshold === null || typeof this._skill.config.sentiment_config?.threshold !== 'number')) {
                    this._skill.config.sentiment_config = null;
                } else {
                    this._skill.config.sentiment_config.config_type = SkillType.SkillSentiment;
                }
            }

            if (this._skill.config.classification_config) {
                if (!Object.keys(this._skill.config.classification_config).some(key => this._skill.config!.classification_config![key as keyof typeof this._skill.config.classification_config])) {
                    this._skill.config.classification_config = null;
                } else {
                    this._skill.config.classification_config.config_type = SkillType.SkillClassification;
                }

            }
        }
    }

    get skill_id(): number | undefined {
        return this._skill.skill_id;
    }

    set skill_id(value: number | undefined) {
        this._skill.skill_id = value;
    }

    get skill(): AgentSkills {
        return this._skill;
    }

    set skill(value: AgentSkills) {
        this._skill = value;
    }

    get config(): AgentSkills['config'] {
        return this._skill.config;
    }

    set config(value: AgentSkills['config']) {
        this._skill.config = value;
    }

    private async uploadFiles(files: File[], folder: string = '') {
        let folderKey = folder;

        if (!folderKey) {
            const resp = await createFolderService(this.knowledge, uuidv4());

            if (resp) {
                folderKey = resp.folder.key;
            }
        }

        if (folderKey) {
            const resFile = await uploadFileService(folderKey, files);
            return { folderKey, filesKeys: resFile.items};
        }

        return { folderKey: "", filesKeys: [] };
    };

    /**
     * Recorre recursivamente un objeto de tipo `AgentSkills` para actualizar las propiedades `folder` basadas en las propiedades `files`.
     *
     * **Descripción:**
     * Esta función se utiliza específicamente para actualizar los folders de un skill. Recibe un objeto de tipo `AgentSkills` y busca dentro de él (y sus subconfiguraciones) todas las propiedades llamadas `files` que sean arreglos no vacíos. Cuando encuentra una, realiza los siguientes pasos:
     *
     * - **Elimina** la propiedad `filesComponent` si existe en el mismo nivel.
     * - **Marca** que existen archivos estableciendo `existFiles` en `true`.
     * - **Sube** los archivos utilizando la función asíncrona `uploadFiles`.
     * - **Asigna** el `folderKey` retornado por `uploadFiles` a la propiedad `folder` en el mismo nivel donde se encontró `files`.
     *
     * **Consideraciones:**
     * - La función es recursiva y explorará todas las propiedades del objeto, incluyendo las anidadas dentro de `AgentSkillsConfig` y sus subconfiguraciones como `RetrieverConfig`, `SummarizeConfig`, etc.
     * - Modifica directamente el objeto `AgentSkills` proporcionado.
     * - Retorna un objeto que incluye el skill actualizado y un valor booleano indicando si se encontraron y procesaron archivos.
     *
     * **Tipos Utilizados:**
     * - `AgentSkills`: Representa un skill con varias configuraciones y propiedades, incluyendo una configuración (`config`) de tipo `AgentSkillsConfig`.
     * - `AgentSkillsConfig`: Incluye múltiples configuraciones y puede contener propiedades como `files`, `action_retriever_config`, `summarize_config`, etc.
     *
     * **Parámetros:**
     * - `skill` (`AgentSkills`): El objeto skill que se va a recorrer y actualizar.
     *
     * **Retorno:**
     * - `Promise<{ existFiles: boolean; skill: AgentSkills }>`: Una promesa que resuelve a un objeto que contiene el skill actualizado y un booleano indicando si se encontraron y procesaron archivos.
     *
     * **Ejemplo de Uso:**
     * ```typescript
     * const skill: AgentSkills = { ... }; // Tu objeto AgentSkills
     * const { existFiles, skill: updatedSkill } = await traverse(skill);
     * if (existFiles) {
     *     console.log('Se encontraron y procesaron archivos en el skill.');
     * }
     * // updatedSkill ahora contiene el skill con los valores actualizados.
     * ```
     *
     * **Dependencias:**
     * - Requiere la función asíncrona `uploadFiles(files: File[]): Promise<string>` que sube los archivos y retorna un `folderKey`.
     *
     * @param {AgentSkills} skill - El objeto `AgentSkills` que se va a recorrer y actualizar.
     * @returns {Promise<{ existFiles: boolean; skill: AgentSkills }>} - Un objeto que contiene el skill actualizado y un booleano indicando si se encontraron y procesaron archivos.
     */
    public async addFolderKeyBySkill(): Promise<{ existFiles: boolean; skill: AgentSkills }> {
        let existFiles = false;

        const traverseObject = async (obj: any) => {
            for (const key in obj) {
                if (obj.hasOwnProperty(key)) {
                    const value = obj[key];
                    if (key === 'files' && Array.isArray(value) && value.length > 0) {
                        // Eliminar 'filesComponent' si existe
                        if (obj.hasOwnProperty('filesComponent')) {
                            delete obj.filesComponent;
                        }
                        existFiles = true;
                        try {
                            const { folderKey, filesKeys } = await this.uploadFiles(value, obj['folder']);
                            if (folderKey) {
                                if (obj.config_type === SkillType.SkillRetriever && !obj.columns_to_find) {
                                    obj.file = filesKeys[0].key;
                                }
                                else if (obj.config_type === SkillType.SkillDataAnalysis) {
                                    obj.source_file = filesKeys[0].key;
                                    obj.source_type = 'csv'
                                }
                                else obj.folder = folderKey;
                            }                            
                            if (filesKeys.length > 0 && obj.source_file === null) {
                                obj.source_file = filesKeys[0].key;
                            }
                        } catch (error) {
                            console.error('Error al subir archivos:', error);
                        }
                    } else if (value && typeof value === 'object') {
                        // Llamada recursiva para objetos anidados
                        await traverseObject(value);
                    }
                }
            }
        };

        await traverseObject(this._skill);

        return { existFiles, skill: this._skill };
    }

    public getSkill(): AgentSkills {
        return { ...this._skill, id: this.skill_id || this._skill.id };
    }

    //
    public isValidSkill(): string {
        if(!this._skill.description) {
            return 'description_required';
        }

        if(!this._skill.prompt) {
            return 'prompt_required';
        }

        if (this._skill.skill_type === SkillType.SkillRetriever && !this.is_structure_retriever) {
            if(!this._skill.config?.files && !this._skill.config?.url) {
                return 'general_knowledge_required';
            }

            if (!this._skill.config?.vector_store) {
                return 'vector_storage_required';
            }
        }

        if (this._skill.skill_type === SkillType.SkillRetriever && this.is_structure_retriever) {

            const validColumnsToReturn = this._skill.config?.columns_to_return && this._skill.config.columns_to_return.length > 0 &&
            this._skill.config?.columns_to_return?.some(
                (column: KeyValue) => column.name === '' || column.value === ''
            );


            if (
                (this.skillRetrieverSelected === EnumSkillRetrieverFields.DATABASE)
                && (
                    !this._skill.config?.db_host || !this._skill.config?.db_name || !this._skill.config?.db_password ||
                    !this._skill.config?.table_name
                ) || validColumnsToReturn
            ) {
                return 'sbx_config_db_required';
            }
            
            if (this.skillRetrieverSelected === EnumSkillRetrieverFields.SBX && (!this._skill.config?.model_name || validColumnsToReturn)) {
                return 'sbx_config_sbx_required';
            }

            if ((this.skillRetrieverSelected === EnumSkillRetrieverFields.EXCEL && !this._skill.config?.files || validColumnsToReturn) ) {
                return 'sbx_config_excel_required';
            }
           
        }

        if (this._skill.skill_type === SkillType.SkillDocumentAnalysis) {
            if (!this._skill.config?.top_words) {
                // toast.error(agent('top_words_required'));
                return 'top_words_required';
            }

            if (!this._skill.config.summarize_config) {
                // toast.error(agent('summarize_config_required'));
                return 'summarize_config_required';
            }

            if (!this._skill.config.summarize_config?.context){
                return 'summary_context_required';
            }

            if (!this._skill.config.summarize_config) {
                // toast.error(agent('summarize_config_required'));
                return 'summarize_config_required';
            }
        }

        if(this._skill.skill_type === SkillType.SkillDataAnalysis) {
          

            const validColumnsToReturn = this._skill.config?.columns_description && this._skill.config.columns_description.length > 0 &&
            this._skill.config?.columns_description?.some(
                (column: KeyValue) => column.name === '' || column.value === ''
            );


            if (
                (this.skillRetrieverSelected === EnumSkillRetrieverFields.DATABASE)
                && (
                    !this._skill.config?.db_host || !this._skill.config?.db_name || !this._skill.config?.db_password || validColumnsToReturn
                ) 
            ) {
                return 'sbx_config_db_required';
            }
            
            if (this.skillRetrieverSelected === EnumSkillRetrieverFields.REPORT && (!this._skill.config?.context || !this._skill.config?.report_key) || validColumnsToReturn) {
                return 'sbx_config_report_required';
            }

            if (this.skillRetrieverSelected === EnumSkillRetrieverFields.EXCEL && (!this._skill.config?.files || this._skill.config?.files?.length === 0 || validColumnsToReturn)) {
                return 'sbx_config_excel_required';
            }

        }

        if (this._skill?.skill_type && [SkillType.SkillDataAnalysis, SkillType.SkillClassification, SkillType.SkillSummarize].includes(this._skill?.skill_type as SkillType)) {
            if (this._skill.config?.action_retriever_config && !this._skill.config?.action_retriever_config?.vector_store) {
                // toast.error(agent('vector_storage_required_on_action_retriever_config'));
                return 'vector_storage_required_on_action_retriever_config';
            }
        }


        return '';
    }

    public async uploadFolders(agent_id: number, user_id: number) {
        let existFiles = false;
        const folderResults = await this.addFolderKeyBySkill();
        existFiles = folderResults.existFiles;

        // upsert folders for agent
        if (existFiles) {
            const response = await upsertAgentSkillServiceV2({
                ...this._skill,
                id: this.skill_id,
            } as AgentSkills, agent_id, false);
            if (!response?.success){
                return response;
            }
        }


        if (this.skill_id && (this._skill.skill_type?.includes('Retriever'))
            && (this._skill?.config?.action_retriever_config?.vector_store || this._skill.config?.url || existFiles)) {

            await learnAgentServiceV2({
                user: user_id.toString(),
                skill_id: this.skill_id?.toString(),
                test: true,
                agent_id: agent_id.toString()
            });
        }

        return {success: true}
    }

    public async learningManual(agent_id: number, user_id: number) {
        if (this.skill_id) {
            const res = await learnAgentServiceV2({
                user: user_id.toString(),
                skill_id: this.skill_id?.toString(),
                test: true,
                agent_id: agent_id.toString(),
            });
            return { success: res.success };
        }
    }

    public async validateAndTranslateSkill() {
        const fields: { key: keyof AgentSkills; keyEn: keyof AgentSkills }[] = [
            { key: 'description', keyEn: 'description_en' },
            { key: 'prompt', keyEn: 'prompt_en' },
            { key: 'output_format', keyEn: 'output_format_en' },
        ];

        await validateAndTranslate<AgentSkills, OldValuesSkills>(this._skill, this.old_values, this._skill, fields);
    }
}